
import { useState, useEffect } from 'react';
import { useAuthenticationContext } from '../contexts/AuthenticationContext';
import { useAPIContext } from '../contexts/APIContext';
import { useFilterContext } from '../contexts/FilterContext';
import MainMenu from '../components/Menus/MainMenu';
import JobTile from '../components/common/JobTile';
import { useJobUtils } from '../hooks/useJobUtils';
import { JOB_STATUSES } from '../configuration/constants';
import { AiOutlineArrowLeft, AiOutlineArrowRight } from 'react-icons/ai';
import { useFetchJobsByStatus } from '../hooks/useFetchJobsByStatus';
import ClipLoader from 'react-spinners/ClipLoader'; // Import the spinner
import { useNavigate } from 'react-router-dom'; // Import useNavigate from react-router-dom
import { usePagination } from '../hooks/usePagination'; // Import the pagination hook

const ToBeAssignedJobs = () => {
  const { employeeAuthenticationData } = useAuthenticationContext();
  const { fetchEmployeeDropDownData, employeeDropDownData } = useAPIContext();
  const { selectedFilters, setSelectedFilters, filterJobs } = useFilterContext();
  const { checkCondition } = useJobUtils();
  const navigate = useNavigate(); // Initialize useNavigate hook

  const [selectedEmployeeEmail, setSelectedEmployeeEmail] = useState('');

  const jobsPerPage = 10;

  // Default to 0 for totalJobs until it is fetched
  const [totalJobs, setTotalJobs] = useState(0);

  // Use the usePagination hook, passing the totalJobs after fetching data
  const { currentPage, totalPages, handleNextPage, handlePreviousPage, handlePageClick, getPageNumbers } = usePagination('currentPageToBeAssigned', totalJobs, jobsPerPage);

  // Prepare params for API request
  const params = employeeAuthenticationData
    ? {
        jobStatusName: JOB_STATUSES['2'], // To be assigned jobs
        employeeLevel: employeeAuthenticationData.levelData.name,
        teamName: employeeAuthenticationData.teamData.name,
        teamId: employeeAuthenticationData.teamData.id,
        employeeEmail: employeeAuthenticationData.email,
        page: currentPage, // Correctly assigning currentPage here
        limit: jobsPerPage,
      }
    : null;

  const {
    jobs: completedJobs,
    loading: fetchJobsLoading,
    error: fetchJobsError,
    totalJobs: fetchedTotalJobs,
  } = useFetchJobsByStatus(params);

  // Update totalJobs after data is fetched
  useEffect(() => {
    if (fetchedTotalJobs !== totalJobs) {
      setTotalJobs(fetchedTotalJobs);
    }
  }, [fetchedTotalJobs, totalJobs]);

  const filteredJobs = filterJobs(completedJobs, selectedFilters);

  // Fetch finance employee dropdown data
  useEffect(() => {
    if (employeeAuthenticationData) {
      fetchEmployeeDropDownData('Finance');
    }
  }, [employeeAuthenticationData, fetchEmployeeDropDownData]);

  const handleEmployeeChange = (event) => {
    const selectedEmployee = employeeDropDownData.find(
      (employee) => employee.id === event.target.value
    );
    if (selectedEmployee) {
      setSelectedEmployeeEmail(selectedEmployee.email);
    }
  };

  // Cleanup effect to reset filters when navigating away
  useEffect(() => {
    return () => {
      setSelectedFilters([]); // Reset filters on unmount or navigation
    };
  }, [navigate, setSelectedFilters]);

  return (
    <div className="p-4 bg-gray-900 text-white min-h-screen">
      <MainMenu
        onSelectFilters={setSelectedFilters}
        showJobStatusFilter={false}
        showDepartmentFilter={true}
        showApproveRejectActions={false}
        employeeEmail={selectedEmployeeEmail}
        page={'ToBeAssignedJobs'}
      />

      {/* Dropdown Menu */}
      {checkCondition('showFinanceDropdown1') && (
        <div className="mt-4">
          <label
            htmlFor="employee-select"
            className="block mb-2 text-sm font-medium text-gray-200"
          >
            Select Employee:
          </label>
          <select
            id="employee-select"
            className="p-2 bg-gray-800 text-white rounded"
            onChange={handleEmployeeChange}
          >
            <option value="">Select an employee</option>
            {employeeDropDownData.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
          </select>
        </div>
      )}

      <div className="mt-4">
        {fetchJobsLoading ? (
          <div className="flex justify-center items-center h-32">
            <ClipLoader color="#ffffff" loading={fetchJobsLoading} size={50} />
          </div>
        ) : fetchJobsError ? (
          <div className="text-center text-red-500">Error: {fetchJobsError}</div>
        ) : (
          <>
            {/* Render job tiles */}
            <div className="mt-4">
              {filteredJobs.map((job) => (
                <JobTile key={job.id} job={job} />
              ))}
            </div>

            {/* Pagination controls */}
            <div className="flex justify-center mt-4 space-x-2">
              {/* Previous arrow */}
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="text-xl text-gray-300 disabled:text-gray-500"
              >
                <AiOutlineArrowLeft />
              </button>

              {/* Page numbers */}
              {getPageNumbers().map((pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => handlePageClick(pageNumber)}
                  className={`px-2 ${currentPage === pageNumber ? 'underline' : 'text-gray-300'}`}
                >
                  {pageNumber}
                </button>
              ))}

              {/* Next arrow */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="text-xl text-gray-300 disabled:text-gray-500"
              >
                <AiOutlineArrowRight />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ToBeAssignedJobs;

